import { useState, useEffect, useMemo, useRef } from "react";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const defaultFormData = {
  business_type: "outsourcing",
  regime_type: "contract",
  contract_type: "outsourcing",
  gross_value: "",
  value_type: "monthly",
  hours_per_month: 168,
  hours_per_day: 8,
  auto_hours_per_month: true,
  fix_fee_12_months: true,
  rv_dependency_level: "over_80",
  irs_type: "single",
  dependents: 0,
  irs_jovem: false,
  irs_jovem_year: 1,
  client_rate: "",
  client_rate_type: "hourly",
  billable_months: 11,
  payment_terms: 30,
  working_days_month: 21.7,
  include_meal_allowance: true,
  meal_allowance_daily: 10.46,
  meal_allowance_card: true,
  meal_allowance_months: 11,
  include_health_insurance: true,
  health_insurance_cost: 55,
  health_insurance_dependents: 0,
  consultant_age: null,
  dependents_ages: [],
  include_performance_bonus: false,
  performance_bonus: 0,
  km_allowance: 0,
  km_allowance_months: 11,
  ajuda_custo_type: "fixed",
  ajuda_custo_km_price: 0.36,
  ajuda_custo_km_count: 0,
  insurance_capitalization: 0,
  insurance_capitalization_months: 12,
  ticket_infancia: 0,
  ticket_infancia_months: 12,
  coverflex: 0,
  coverflex_months: 12,
  equipment_id: "",
  equipment_ids: [],
  license_id: "",
  client_special_fee_id: "",
  is_fix_fee: false,
  fix_fee_value: 0,
  include_iht: true,
  include_welcome_kit: true,
  include_agency_fee: false,
  include_manual_premium: false,
  manual_premium_value: 0,
  placement_type: "national",
  intl_travel: 0,
  intl_travel_months: 11,
  intl_lawyers: 0,
  intl_lawyers_months: 11,
  intl_visa: 0,
  intl_visa_months: 11,
  intl_family_reunification: 0,
  intl_family_reunification_months: 11,
  vacation_days: 22,
  daily_rate: "",
  first_year_ss_exempt: false,
  payment_months: 11,
  perm_candidate_salary: "",
  perm_salary_type: "annual",
  perm_fee_type: "percentage",
  perm_percentage_fee: 15,
  perm_fixed_fee: 0,
  selected_benefits: [],
};

// LocalStorage key for persisting the simulator form state across navigation.
// The state is cleared ONLY when the user explicitly clicks "Limpar"; navigating
// to /candidates, /admin, etc. and back must preserve everything the user typed.
// Nota GDPR: usamos sessionStorage (não localStorage) porque este estado contém
// dados pessoais do candidato (nome, NIF, email). sessionStorage é limpo ao fechar
// a aba/browser, reduzindo exposição em máquinas partilhadas. Não persistente entre sessões.
const LS_KEY = "evolutt:simulator:state:v1";

const loadPersistedState = () => {
  try {
    const raw = window.sessionStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

export const useSimulatorForm = () => {
  const persisted = typeof window !== "undefined" ? loadPersistedState() : null;
  const [formData, setFormData] = useState(() => ({ ...defaultFormData, ...(persisted?.formData || {}) }));
  const [consultantName, setConsultantName] = useState(() => persisted?.consultantName || "");
  const [consultantBirthDate, setConsultantBirthDate] = useState(() => persisted?.consultantBirthDate || "");
  const [consultantNif, setConsultantNif] = useState(() => persisted?.consultantNif || "");
  const [consultantEmail, setConsultantEmail] = useState(() => persisted?.consultantEmail || "");
  const [consultantFunction, setConsultantFunction] = useState(() => persisted?.consultantFunction || "");
  const [reverseMode, setReverseMode] = useState(() => !!persisted?.reverseMode);
  const [desiredNetMonthly, setDesiredNetMonthly] = useState(() => persisted?.desiredNetMonthly || "");
  // Timestamp of the last persisted change. Hydrate from localStorage on first
  // mount so that the "draft saved Xmin ago" indicator survives page reloads.
  const [savedAt, setSavedAt] = useState(() => persisted?.savedAt || null);
  // Ref tracking the last serialised payload we wrote to localStorage (without
  // the savedAt field). When sibling hooks (useSimulatorData / templates) call
  // setFormData with derived values that produce the same payload byte-for-byte,
  // we MUST NOT refresh savedAt — otherwise the "há Xmin" label perpetually
  // resets to "agora mesmo" and never reflects real user idle time.
  const lastPayloadRef = useRef(() => {
    // Initialise from persisted snapshot so a first-mount no-op equality check
    // doesn't bump savedAt to Date.now() right after hydration.
    if (!persisted) return null;
    const { savedAt: _omit, ...rest } = persisted;
    try { return JSON.stringify(rest); } catch { return null; }
  });
  // Resolve the ref's initial lazy function (useRef doesn't support lazy init
  // natively — we keep it null when the lazy fn was used and resolve once).
  if (typeof lastPayloadRef.current === "function") {
    lastPayloadRef.current = lastPayloadRef.current();
  }

  // Persist the full state so navigating to other pages and back keeps the
  // simulator exactly as the user left it. savedAt is refreshed ONLY when the
  // serialised payload actually changes vs the previously persisted snapshot.
  useEffect(() => {
    const payload = {
      formData,
      consultantName,
      consultantBirthDate,
      consultantNif,
      consultantEmail,
      consultantFunction,
      reverseMode,
      desiredNetMonthly,
    };
    let serialised;
    try { serialised = JSON.stringify(payload); } catch { return; }
    if (serialised === lastPayloadRef.current) return; // no real change, keep savedAt
    lastPayloadRef.current = serialised;
    const ts = Date.now();
    try {
      window.sessionStorage.setItem(LS_KEY, JSON.stringify({ ...payload, savedAt: ts }));
      setSavedAt(ts);
    } catch { /* quota/SecurityError — ignore */ }
  }, [formData, consultantName, consultantBirthDate, consultantNif, consultantEmail, consultantFunction, reverseMode, desiredNetMonthly]);

  const resetAll = () => {
    setFormData(defaultFormData);
    setConsultantName("");
    setConsultantBirthDate("");
    setConsultantNif("");
    setConsultantEmail("");
    setConsultantFunction("");
    setReverseMode(false);
    setDesiredNetMonthly("");
    setSavedAt(null);
    lastPayloadRef.current = null;
    try { window.sessionStorage.removeItem(LS_KEY); } catch { /* ignore */ }
  };

  // True when the user has typed any meaningful value (i.e. has a real draft).
  // Used by the "Rascunho guardado" indicator to avoid showing on a pristine
  // form. Considers consultant info OR a non-empty proposal value.
  const hasDraft = !!(
    consultantName || consultantNif || consultantEmail || consultantBirthDate || consultantFunction ||
    formData.gross_value || formData.client_rate || formData.perm_candidate_salary || formData.daily_rate
  );

  const debouncedFormData = useDebounce(formData, 500);
  const debouncedDesiredNet = useDebounce(desiredNetMonthly, 500);

  const calculatedRates = useMemo(() => {
    const rate = parseFloat(formData.client_rate) || 0;
    const hoursDay = parseInt(formData.hours_per_day) || 8;
    const daysMonth = parseFloat(formData.working_days_month) || 21.7;
    const hoursMonth = parseInt(formData.hours_per_month) || 168;
    const billableMonths = parseFloat(formData.billable_months) || 11;

    let hourly, daily, monthly;
    if (formData.client_rate_type === "hourly") {
      hourly = rate;
      daily = rate * hoursDay;
      monthly = rate * hoursMonth;
    } else if (formData.client_rate_type === "daily") {
      hourly = rate / hoursDay;
      daily = rate;
      monthly = rate * daysMonth;
    } else {
      hourly = rate / hoursMonth;
      daily = rate / daysMonth;
      monthly = rate;
    }
    return { hourly, daily, monthly, annual: monthly * billableMonths };
  }, [formData.client_rate, formData.client_rate_type, formData.hours_per_day, formData.working_days_month, formData.hours_per_month, formData.billable_months]);

  const calculatedPermFee = useMemo(() => {
    const salary = parseFloat(formData.perm_candidate_salary) || 0;
    const percentageFee = formData.perm_fee_type !== "fixed" ? salary * (parseFloat(formData.perm_percentage_fee) || 0) / 100 : 0;
    const fixedFee = formData.perm_fee_type !== "percentage" ? parseFloat(formData.perm_fixed_fee) || 0 : 0;
    return { percentageFee, fixedFee, totalFee: percentageFee + fixedFee };
  }, [formData.perm_candidate_salary, formData.perm_fee_type, formData.perm_percentage_fee, formData.perm_fixed_fee]);

  const isPerm = formData.business_type === "perm";
  const isFreelancer = formData.regime_type === "green_receipts" || formData.regime_type === "b2b";
  const isContract = formData.regime_type === "contract";

  return {
    formData, setFormData,
    resetAll,
    consultantName, setConsultantName,
    consultantBirthDate, setConsultantBirthDate,
    consultantNif, setConsultantNif,
    consultantEmail, setConsultantEmail,
    consultantFunction, setConsultantFunction,
    reverseMode, setReverseMode,
    desiredNetMonthly, setDesiredNetMonthly,
    debouncedFormData, debouncedDesiredNet,
    calculatedRates, calculatedPermFee,
    isPerm, isFreelancer, isContract,
    savedAt, hasDraft,
  };
};
